import { settings as settingsStore, planStates, plans, habits, events as eventsStore } from '../storage/db.js';
import type { AppEvent, IntervalState, UserSettings } from '../core/types.js';
import { minutesSinceLocalMidnight, localDate } from '../core/time.js';
import { track } from '../analytics/ga.js';

/**
 * Conservative notification scheduler.
 *
 * Browser tabs cannot reliably fire notifications when the page is fully
 * closed without a backend push service, so this scheduler only fires while
 * the app is open. That matches docs/19's "open or recently active" guidance.
 */

let intervalHandle: number | null = null;
let firedClearHandle: number | null = null;
const FIRED = new Set<string>();

export function notificationsSupported(): boolean {
  return typeof Notification !== 'undefined';
}

export async function requestPermission(): Promise<NotificationPermission> {
  if (!notificationsSupported()) {
    track('notification_permission', { outcome: 'unsupported' });
    return 'denied';
  }
  if (Notification.permission === 'granted' || Notification.permission === 'denied') {
    return Notification.permission;
  }
  const result = await Notification.requestPermission();
  track('notification_permission', { outcome: result });
  return result;
}

function show(title: string, body: string, tag: string, inAppOnly: boolean): void {
  if (FIRED.has(tag)) return;
  FIRED.add(tag);
  if (inAppOnly) {
    window.dispatchEvent(new CustomEvent('wean:notification', {
      detail: { title, body, tag },
    }));
    return;
  }
  if (!notificationsSupported() || Notification.permission !== 'granted') return;
  try {
    new Notification(title, {
      body,
      tag,
      silent: false,
      icon: './icons/icon.svg',
    });
  } catch { /* notification quota or environment limits — ignore */ }
}

/**
 * Returns the start hour (0-23) of the 3-hour window that holds the most
 * usage events in the supplied window, or null when there isn't enough data
 * or the cluster is too diffuse (<50% of events).
 */
export function hardestHourWindow(events: AppEvent[], lookbackMs: number = 14 * 86_400_000): number | null {
  const cutoff = Date.now() - lookbackMs;
  const uses = events.filter((e) =>
    e.type === 'usage_logged' && e.status !== 'undone' && new Date(e.occurredAt).getTime() >= cutoff,
  );
  if (uses.length < 4) return null;
  const counts = new Array<number>(24).fill(0);
  for (const e of uses) counts[new Date(e.occurredAt).getHours()] = (counts[new Date(e.occurredAt).getHours()] ?? 0) + 1;
  let bestStart = 0;
  let bestSum = 0;
  for (let start = 0; start < 24; start++) {
    const sum = (counts[start] ?? 0) + (counts[(start + 1) % 24] ?? 0) + (counts[(start + 2) % 24] ?? 0);
    if (sum > bestSum) { bestSum = sum; bestStart = start; }
  }
  if (bestSum / uses.length < 0.5) return null;
  return bestStart;
}

export function isInQuietHours(s: UserSettings, nowMinutes: number = minutesSinceLocalMidnight()): boolean {
  const start = s.quietHoursStart;
  const end = s.quietHoursEnd;
  if (start === undefined || end === undefined) return false;
  if (start === end) return false;
  // Same-day window: 09:00 → 17:00 means quiet when start <= now < end.
  // Overnight window: 22:00 → 07:00 means quiet when now >= start OR now < end.
  if (start < end) return nowMinutes >= start && nowMinutes < end;
  return nowMinutes >= start || nowMinutes < end;
}

async function tick(): Promise<void> {
  const s: UserSettings = await settingsStore.get();
  if (!s.notificationsEnabled) return;
  if (isInQuietHours(s)) return;
  // Default-on for target reached when notifications are enabled; user can
  // opt out via Settings → Notifications.
  const targetEnabled = s.notifyTargetReached !== false;
  if (!targetEnabled) return;
  const allHabits = await habits.all();
  const allPlans = await plans.all();
  const allStates = await planStates.all();
  for (const h of allHabits) {
    if (!h.active) continue;
    const plan = allPlans.find((p) => p.habitId === h.id && p.active);
    if (!plan) continue;
    if (plan.lifecycleState === 'paused' || plan.lifecycleState === 'graduated' || plan.lifecycleState === 'archived') continue;
    const ps = allStates.find((x) => x.planId === plan.id);
    if (!ps) continue;
    if (ps.state.planType !== 'interval') continue;
    const st = ps.state as IntervalState;
    if (!st.lastUseAt) continue;
    const elapsed = Date.now() - new Date(st.lastUseAt).getTime();
    const remain = st.targetIntervalMs - elapsed;
    const tag = `target-${plan.id}-${st.lastUseAt}`;
    if (remain <= 0 && remain > -60_000) {
      const title = s.privacyMode ? 'Plan' : (h.privateLabel ?? h.label);
      const body = s.privacyMode ? 'Time for your check-in.' : 'Target reached. You did the wait.';
      show(title, body, tag, s.notificationsInAppOnly === true);
    }
  }

  // Risk-window prep: opt-in only. Fire ~30 min before the hardest 3h
  // window once per local date per habit. Detection uses a 14-day lookback.
  if (s.notifyRiskWindow) {
    const nowMinutes = minutesSinceLocalMidnight();
    for (const h of allHabits) {
      if (!h.active) continue;
      const habitEvents = await eventsStore.byHabit(h.id);
      const windowStart = hardestHourWindow(habitEvents);
      if (windowStart === null) continue;
      const windowStartMinutes = windowStart * 60;
      const minutesUntil = (windowStartMinutes - nowMinutes + 24 * 60) % (24 * 60);
      // Fire when 25-35 minutes before the window — wider than tick interval.
      if (minutesUntil < 25 || minutesUntil > 35) continue;
      const tag = `risk-${h.id}-${localDate()}`;
      const title = s.privacyMode ? 'Plan' : (h.privateLabel ?? h.label);
      const startLabel = formatHour(windowStart);
      const body = s.privacyMode
        ? 'Take a pause before the usual time.'
        : `Risky window near ${startLabel}. A short rescue beats willpower.`;
      show(title, body, tag, s.notificationsInAppOnly === true);
    }
  }
}

function formatHour(h: number): string {
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hh = ((h + 11) % 12) + 1;
  return `${hh} ${ampm}`;
}

export function startNotificationLoop(): void {
  if (intervalHandle != null) return;
  intervalHandle = window.setInterval(() => { tick().catch(() => {}); }, 30_000);
  // Reset fired tags daily so the same target time can fire on a future plan.
  firedClearHandle = window.setInterval(() => FIRED.clear(), 24 * 3600_000);
}

export function stopNotificationLoop(): void {
  if (intervalHandle != null) { window.clearInterval(intervalHandle); intervalHandle = null; }
  if (firedClearHandle != null) { window.clearInterval(firedClearHandle); firedClearHandle = null; }
}
